/***
 * WebAuthn server-side: backend conserva la public key, emette token solo
 * dopo verifica della firma biometrica. La biometria è legata all'identità
 * utente, non a un token cached → sopravvive a logout volontario.
 */

const ENABLED_KEY      = 'ch_biometric_enabled';
const USER_HANDLE_KEY  = 'ch_biometric_user'; // c_utente registrato su questo device
const SESSION_KEY      = 'ch_biometric_session_ok';

// ─── Disponibilità ───────────────────────────────────────────────────────────

export const isSupported = () =>
    typeof window !== 'undefined' && !!window.PublicKeyCredential;

export const canUsePlatformAuth = async () => {
    if (!isSupported()) return false;
    try {
        return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
        return false;
    }
};

// ─── WebAuthn: create (registrazione) ────────────────────────────────────────

/**
 * Esegue navigator.credentials.create() con le options ricevute dal server.
 * Ritorna l'oggetto serializzabile da inviare al server per la verifica.
 * @param {object} options - PublicKeyCredentialCreationOptionsJSON dal server
 */
export const createCredential = async (options) => {
    const publicKey = _decodeCreationOptions(options);
    const cred = await navigator.credentials.create({ publicKey });
    return _encodeAttestationResponse(cred);
};

/**
 * Esegue navigator.credentials.get() con le options ricevute dal server.
 * Ritorna l'oggetto serializzabile da inviare al server per la verifica.
 * @param {object} options - PublicKeyCredentialRequestOptionsJSON dal server
 */
export const getCredential = async (options) => {
    const publicKey = _decodeRequestOptions(options);
    const cred = await navigator.credentials.get({ publicKey });
    return _encodeAssertionResponse(cred);
};

// ─── Stato ───────────────────────────────────────────────────────────────────

export const isEnabled    = ()        => localStorage.getItem(ENABLED_KEY) === 'true';
export const setEnabled   = (userId)  => {
    localStorage.setItem(ENABLED_KEY, 'true');
    localStorage.setItem(USER_HANDLE_KEY, String(userId));
};
export const getUserHandle = ()       => localStorage.getItem(USER_HANDLE_KEY);

// ─── Sessione verificata ─────────────────────────────────────────────────────
// localStorage con timestamp: su Android PWA sessionStorage viene azzerato
// quando il sistema killa il processo WebView in background.

const SESSION_TTL_MS = 8 * 60 * 60 * 1000; // 8 ore

export const isSessionVerified = () => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    return (Date.now() - ts) < SESSION_TTL_MS;
};
export const markSessionVerified = () => localStorage.setItem(SESSION_KEY, String(Date.now()));
export const clearSessionVerified= () => localStorage.removeItem(SESSION_KEY);

/** Rimuove la registrazione biometrica locale. Il backend va notificato a parte. */
export const remove = () => {
    localStorage.removeItem(ENABLED_KEY);
    localStorage.removeItem(USER_HANDLE_KEY);
    localStorage.removeItem(SESSION_KEY);
};

// ─── Helpers base64url ←→ ArrayBuffer ───────────────────────────────────────

function _b64urlToBuffer(b64url) {
    const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
    const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer;
}

function _bufferToB64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function _decodeCreationOptions(options) {
    return {
        ...options,
        challenge: _b64urlToBuffer(options.challenge),
        user: {
            ...options.user,
            id: _b64urlToBuffer(options.user.id),
        },
        excludeCredentials: (options.excludeCredentials || []).map(c => ({
            ...c,
            id: _b64urlToBuffer(c.id),
        })),
    };
}

function _decodeRequestOptions(options) {
    return {
        ...options,
        challenge: _b64urlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map(c => ({
            ...c,
            id: _b64urlToBuffer(c.id),
        })),
    };
}

function _encodeAttestationResponse(cred) {
    return {
        id: cred.id,
        rawId: _bufferToB64url(cred.rawId),
        type: cred.type,
        response: {
            attestationObject: _bufferToB64url(cred.response.attestationObject),
            clientDataJSON: _bufferToB64url(cred.response.clientDataJSON),
        },
        clientExtensionResults: cred.getClientExtensionResults?.() || {},
    };
}

function _encodeAssertionResponse(cred) {
    return {
        id: cred.id,
        rawId: _bufferToB64url(cred.rawId),
        type: cred.type,
        response: {
            authenticatorData: _bufferToB64url(cred.response.authenticatorData),
            clientDataJSON:    _bufferToB64url(cred.response.clientDataJSON),
            signature:         _bufferToB64url(cred.response.signature),
            userHandle: cred.response.userHandle ? _bufferToB64url(cred.response.userHandle) : null,
        },
        clientExtensionResults: cred.getClientExtensionResults?.() || {},
    };
}
